import React, { useMemo, useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, Menu, X, ArrowRight, LogOut, LogIn, FileText, ClipboardCheck, Zap, Car } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useVehicle } from '../contexts/VehicleContext';
import { vehicleLabel } from '../lib/plate';
import { SITE } from '../config/site';
import { fetchCategoriesTree } from '../services/productService';
import CategoryMenu from './CategoryMenu';
import { isAuthenticated, getCurrentUser, logout } from '../lib/authUtils';
import { toast } from '../hooks/use-toast';

const Header = () => {
  const { getCartCount, wishlist, compareList } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { vehicle, clearVehicle } = useVehicle();
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // The category tree is only needed for the Shop mega menu, so load it on first hover/focus (or when the browser is idle).
  const treeRequested = React.useRef(false);
  const loadCategoriesTree = React.useCallback(async () => {
    if (treeRequested.current) return;
    treeRequested.current = true;
    setLoadingTree(true);
    try { setCategoriesTree(await fetchCategoriesTree()); } catch (error) { setCategoriesTree([]); } finally { setLoadingTree(false); }
  }, []);
  useEffect(() => {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2500));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = idle(loadCategoriesTree);
    return () => cancel(id);
  }, [loadCategoriesTree]);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isAuthenticated();
      setIsLoggedIn(authenticated);
      if (authenticated) {
        setCurrentUser(getCurrentUser());
      } else {
        setCurrentUser(null);
      }
    };
    
    checkAuth();
    // Listen for storage changes (when user logs in/out in another tab)
    window.addEventListener('storage', checkAuth);
    // Listen for custom auth state change event
    window.addEventListener('authStateChanged', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authStateChanged', checkAuth);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setUserMenuOpen(false);
    toast({
      title: 'Logged Out',
      description: 'You have been successfully logged out.',
    });
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return;
    
    const handleClickOutside = (event) => {
      const menuElement = document.querySelector('[data-user-menu]');
      if (menuElement && !menuElement.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-gray-100 border-b">
        <div className="container mx-auto px-4 py-2">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-4">
              <span className="text-gray-600 truncate max-w-[60vw] sm:max-w-none">{isLoggedIn && currentUser ? `${currentUser.customerAccountName || currentUser.company || 'Your account'}${currentUser.pricingProfileName ? ` · ${currentUser.pricingProfileName} pricing` : ''}${currentUser.customerAccount?.paymentTerms ? ` · ${currentUser.customerAccount.paymentTerms} terms` : ''}` : 'Fast dispatch from Sydney and Melbourne · Sign in for your account pricing'}</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600 hidden sm:inline">Call us</span>
              <a href={`tel:${SITE.phone.replace(/\s/g, '')}`} className="font-semibold text-red-600">{SITE.phone}</a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt={`${SITE.shortName} logo`} className="h-10 sm:h-12" width="140" height="48" />
          </Link>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-8">
            <div className="flex w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search batteries, parts, brands or SKU…"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:border-red-500"
              />
              <button
                type="submit"
                className="bg-red-600 text-white px-6 py-2 rounded-r hover:bg-red-700 transition"
              >
                <Search size={20} />
              </button>
            </div>
          </form>

          {vehicle && (
            <Link to="/products?fit=1" title="Showing parts that fit this vehicle" className="hidden lg:flex items-center gap-2 mr-4 max-w-[220px] rounded border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs hover:border-red-500">
              <Car size={16} className="text-red-600 shrink-0" />
              <span className="truncate font-semibold text-gray-800">{vehicleLabel(vehicle)}</span>
            </Link>
          )}

          {/* Action Icons */}
          <div className="flex items-center space-x-4 sm:space-x-6">
            <Link to="/wishlist" className="relative hover:text-red-600 transition">
              <Heart size={24} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <Link to="/compare" className="relative hover:text-red-600 transition">
              <ArrowRight size={24} />
              {compareList.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {compareList.length}
                </span>
              )}
            </Link>
            <Link to="/cart" className="relative hover:text-red-600 transition">
              <ShoppingCart size={24} />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Link>
            
            {/* User Menu */}
            <div className="relative" data-user-menu>
              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center space-x-2 hover:text-red-600 transition"
                  >
                    <User size={24} />
                    {currentUser && (
                      <span className="hidden md:block text-sm font-medium text-gray-700">
                        {currentUser.firstName || currentUser.userName || 'Account'}
                      </span>
                    )}
                  </button>
                  
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50" data-user-menu>
                      {currentUser && (
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-semibold text-gray-900">
                            {currentUser.firstName && currentUser.lastName
                              ? `${currentUser.firstName} ${currentUser.lastName}`
                              : currentUser.userName || 'User'}
                          </p>
                          {currentUser.email && (
                            <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
                          )}
                        </div>
                      )}
                      <Link
                        to="/account"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-gray-700"
                      >
                        <User size={16} />
                        <span>My Account</span>
                      </Link>
                      <Link to="/quick-order" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-gray-700"><Zap size={16} /><span>Quick Order</span></Link>
                      <Link to="/quotes" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-gray-700"><FileText size={16} /><span>My Quotes</span></Link>
                      <Link to="/approvals" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-gray-700"><ClipboardCheck size={16} /><span>Approvals</span></Link>
                      <Link
                        to="/orders"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-gray-700"
                      >
                        <ShoppingCart size={16} />
                        <span>My Orders</span>
                      </Link>
                      <div className="border-t border-gray-200 mt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-red-600"
                        >
                          <LogOut size={16} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/customer-login"
                  className="flex items-center space-x-2 hover:text-red-600 transition"
                >
                  <LogIn size={24} />
                  <span className="hidden md:block text-sm font-medium">Login</span>
                </Link>
              )}
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <form onSubmit={handleSearch} className="md:hidden mt-4">
          <div className="flex w-full">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:border-red-500"
            />
            <button
              type="submit"
              className="bg-red-600 text-white px-6 py-2 rounded-r hover:bg-red-700 transition"
            >
              <Search size={20} />
            </button>
          </div>
        </form>
      </div>

      {/* Navigation */}
      <nav className="bg-gray-50 border-t hidden md:block">
        <div className="container mx-auto px-4">
          <ul className="flex space-x-8 py-3">
            <li>
              <NavLink to="/" end={true} className={({ isActive }) => `transition font-medium pb-1 border-b-2 ${isActive ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}>
                Home
              </NavLink>
            </li>
            <li className="relative group" onMouseEnter={loadCategoriesTree} onFocus={loadCategoriesTree}>
              <NavLink to="/products" end={false} className={({ isActive }) => `transition font-medium pb-1 border-b-2 ${isActive ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}>
                Shop
              </NavLink>
              {/* Mega Menu */}
              <div className="absolute left-0 top-full hidden group-hover:block bg-white shadow-lg border mt-0 w-screen max-w-5xl p-6 rounded-b z-50">
                <CategoryMenu categoriesTree={categoriesTree} loadingTree={loadingTree} />
              </div>
            </li>
            {isLoggedIn && (
              <li>
                <NavLink to="/quick-order" end={false} className={({ isActive }) => `transition font-medium pb-1 border-b-2 ${isActive ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}>
                  Quick Order
                </NavLink>
              </li>
            )}
            <li>
              <NavLink to="/quotes" end={false} className={({ isActive }) => `transition font-medium pb-1 border-b-2 ${isActive ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}>
                Quotes
              </NavLink>
            </li>
            {isLoggedIn && (
              <li>
                <NavLink to="/approvals" end={false} className={({ isActive }) => `transition font-medium pb-1 border-b-2 ${isActive ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}>
                  Approvals
                </NavLink>
              </li>
            )}
            <li>
              <NavLink to="/orders" end={false} className={({ isActive }) => `transition font-medium pb-1 border-b-2 ${isActive ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}>
                Orders
              </NavLink>
            </li>
            <li>
              <NavLink to="/blog" end={false} className={({ isActive }) => `transition font-medium pb-1 border-b-2 ${isActive ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}>
                Blog
              </NavLink>
            </li>
            <li>
              <NavLink to="/contact" end={false} className={({ isActive }) => `transition font-medium pb-1 border-b-2 ${isActive ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}>
                Contact
              </NavLink>
            </li>
          </ul>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <nav className="container mx-auto px-4 py-4">
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Shop
                </Link>
              </li>
              {isLoggedIn && (
                <li>
                  <Link to="/quick-order" className="block text-gray-700 hover:text-red-600 transition font-medium" onClick={() => setMobileMenuOpen(false)}>
                    Quick Order
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/quotes"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Quotes
                </Link>
              </li>
              {isLoggedIn && (
                <li>
                  <Link to="/approvals" className="block text-gray-700 hover:text-red-600 transition font-medium" onClick={() => setMobileMenuOpen(false)}>
                    Approvals
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/orders"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Orders
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
              </li>
              <li className="border-t pt-3 mt-3">
                {isLoggedIn ? (
                  <>
                    <Link
                      to="/account"
                      className="block text-gray-700 hover:text-red-600 transition font-medium mb-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Account
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left text-red-600 hover:text-red-700 transition font-medium"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/customer-login"
                    className="block text-gray-700 hover:text-red-600 transition font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login / Sign Up
                  </Link>
                )}
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
